// src/components/PortfolioSelector.jsx
import React, { useState, useEffect } from 'react';
import { useInstanceStore } from '../stores/UseInstanceStore';
import { useModalAlert } from './ModalAlert';
import { useWallet } from '../hooks/useWallet';
import AgentKeyModal from './AgentKeyModal';
import { hasAgentPrivateKey, setAgentPrivateKey } from '../utils/privateKey';

// ─── Two-word slug generator ─────────────────────────────────────────────────
const SLUG_WORDS = [
  ['amber','bolt','calm','dark','ember','frost','gold','haze','iron','jade',
   'keen','lux','mist','nova','onyx','pine','quick','raven','steel','teal',
   'ultra','void','wave','xenon','yield','zinc'],
  ['apex','blade','chain','dawn','echo','flare','grid','hawk','ion','jet',
   'kite','lark','moon','node','orbit','peak','quark','ridge','sail','tide',
   'umber','vex','wind','xray','yarn','zone'],
];
function generateSlug() {
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
  return `${pick(SLUG_WORDS[0])}-${pick(SLUG_WORDS[1])}`;
}

// ─── Wizard step indices ────────────────────────────────────────────────────
const STEP_INFO      = 0;
const STEP_AGENT     = 1;
const STEP_BUILDER   = 2;
const STEP_DONE      = 3;

// ─── Step indicator ─────────────────────────────────────────────────────────
function StepIndicator({ currentStep, isHyperliquid }) {
  const steps = isHyperliquid
    ? ['Portfolio Info', 'Agent Setup', 'Approve Fee', 'Done']
    : ['Portfolio Info', 'Done'];

  const stepIndices = isHyperliquid
    ? [STEP_INFO, STEP_AGENT, STEP_BUILDER, STEP_DONE]
    : [STEP_INFO, STEP_DONE];

  return (
    <div className="flex items-center justify-center gap-0 mb-6 px-2">
      {steps.map((label, i) => {
        const isDone = currentStep > stepIndices[i];
        const isActive = currentStep === stepIndices[i];
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors
                ${isDone ? 'bg-green-500 border-green-500 text-white'
                  : isActive ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'}`}>
                {isDone
                  ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  : i + 1}
              </div>
              <span className={`mt-1 text-[10px] font-medium whitespace-nowrap hidden sm:block
                ${isActive ? 'text-blue-600 dark:text-blue-400'
                  : isDone ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-400 dark:text-gray-500'}`}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 mb-3 sm:mb-5 transition-colors
                ${currentStep > stepIndices[i] ? 'bg-green-400' : 'bg-gray-200 dark:bg-gray-700'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Agent choice: Create new or select existing ─────────────────────────────
function AgentChoiceField({ agents, loadingAgents, formData, onChange, isSubmitting }) {
  // Default to 'create' when no agents exist, otherwise 'select'
  const [mode, setMode] = useState(() => agents.length === 0 ? 'create' : 'select');

  const switchMode = (next) => {
    setMode(next);
    if (next === 'create') {
      // Clear any selected agent so handleSubmitInfo triggers onRequestAgentOnboard
      onChange({ target: { name: 'primary_agent_id', value: '' } });
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Primary Agent
      </label>

      {/* Mode toggle */}
      <div className="flex rounded-lg overflow-hidden border border-slate-600 mb-3">
        <button
          type="button"
          onClick={() => switchMode('create')}
          disabled={isSubmitting}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
            mode === 'create'
              ? 'bg-cyan-700 text-white'
              : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
          }`}
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create New Agent
        </button>
        <button
          type="button"
          onClick={() => switchMode('select')}
          disabled={isSubmitting}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors border-l border-slate-600 disabled:opacity-50 ${
            mode === 'select'
              ? 'bg-cyan-700 text-white'
              : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200'
          }`}
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Select Existing
        </button>
      </div>

      {/* Create mode: descriptive card */}
      {mode === 'create' && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-cyan-950/40 border border-cyan-800/50">
          <div className="w-8 h-8 rounded-full bg-cyan-700/40 border border-cyan-600/40 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-4 h-4 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-cyan-200">Agent setup wizard will open</p>
            <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
              After creating this portfolio, a step-by-step wizard will help you name your agent, choose a strategy, run first research, and get a trade recommendation.
            </p>
          </div>
        </div>
      )}

      {/* Select mode: existing dropdown or empty state */}
      {mode === 'select' && (
        agents.length === 0 ? (
          <div className="p-3 rounded-lg border border-amber-700/50 bg-amber-950/30 text-sm text-amber-300">
            No agents yet.{' '}
            <button type="button" onClick={() => switchMode('create')} className="underline hover:no-underline text-amber-200">
              Create one with the wizard instead.
            </button>
          </div>
        ) : (
          <select
            name="primary_agent_id"
            value={formData.primary_agent_id}
            onChange={onChange}
            disabled={isSubmitting || loadingAgents}
            className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 disabled:opacity-50 text-sm"
          >
            <option value="">Select an agent…</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        )
      )}
    </div>
  );
}

// ─── Step 0: Portfolio Info ──────────────────────────────────────────────────
function StepInfo({ formData, onChange, onSuggestName, agents, loadingAgents, error, isSubmitting }) {
  const [slug, setSlug] = useState(() => generateSlug());

  const refreshSlug = () => setSlug(generateSlug());

  return (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Portfolio Name
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={onChange}
          disabled={isSubmitting}
          placeholder="e.g. My BTC Strategy"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
        />
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className="text-xs text-gray-400 dark:text-gray-500">suggest:</span>
          <button
            type="button"
            onClick={() => { onSuggestName(slug); refreshSlug(); }}
            disabled={isSubmitting}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-mono disabled:opacity-40"
          >
            {slug}
          </button>
          <button
            type="button"
            onClick={refreshSlug}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40"
            title="New suggestion"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      <input type="hidden" name="portfolio_type" value="HL_PAPER" />

      {/* Amount */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Initial Amount ($)
        </label>
        <input
          type="number"
          name="amount"
          value={formData.amount}
          onChange={onChange}
          disabled={isSubmitting}
          min="0"
          step="0.01"
          placeholder="10000"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
        />
      </div>

      {/* Primary Agent — Create or Select */}
      <AgentChoiceField
        agents={agents}
        loadingAgents={loadingAgents}
        formData={formData}
        onChange={onChange}
        isSubmitting={isSubmitting}
      />

      {error && <ErrorBox message={error} />}
    </div>
  );
}

// ─── Step 1: Agent Setup ─────────────────────────────────────────────────────
function StepAgent({ createdAgent, agentMode, onSetMode, importedKey, onImportedKeyChange, importError, onCopy }) {
  const [showKey, setShowKey] = useState(false);
  const address = createdAgent?.address;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        A dedicated <strong className="text-gray-800 dark:text-gray-100">agent wallet</strong> controls trading on your behalf.
        You can use the one we generated, or supply your own private key.
      </p>

      {/* Mode toggle */}
      <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
        <button
          type="button"
          onClick={() => onSetMode('generated')}
          className={`flex-1 py-2 text-sm font-medium transition-colors ${agentMode === 'generated'
            ? 'bg-blue-600 text-white'
            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}
        >
          Use Generated Key
        </button>
        <button
          type="button"
          onClick={() => onSetMode('import')}
          className={`flex-1 py-2 text-sm font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${agentMode === 'import'
            ? 'bg-blue-600 text-white'
            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}
        >
          Import My Key
        </button>
      </div>

      {agentMode === 'generated' ? (
        <>
          {/* Agent Address */}
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
            <p className="text-xs font-medium text-green-700 dark:text-green-300 mb-2 uppercase tracking-wide">
              Agent Address
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 block p-2 text-xs font-mono bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 text-gray-800 dark:text-gray-200 rounded overflow-x-auto">
                {address}
              </code>
              <button
                type="button"
                onClick={() => onCopy(address)}
                className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title="Copy address"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Next-steps instructions */}
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">Next Steps on Hyperliquid</p>
            <ol className="text-sm text-yellow-700 dark:text-yellow-300 list-decimal list-inside space-y-1">
              <li>
                Go to{' '}
                <a
                  href={createdAgent?.isTestnet ? 'https://app.hyperliquid-testnet.xyz' : 'https://app.hyperliquid.xyz'}
                  target="_blank" rel="noopener noreferrer"
                  className="underline hover:no-underline"
                >
                  Hyperliquid {createdAgent?.isTestnet ? 'Testnet' : 'Platform'}
                </a>
              </li>
              <li>Navigate to Account → Agent Trading</li>
              <li>Paste the agent address above and approve it</li>
              <li>Return here and click Next</li>
            </ol>
          </div>
        </>
      ) : (
        /* Import mode */
        <div className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Paste a 0x-prefixed 32-byte private key (66 characters) for an agent wallet you already control.
          </p>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={importedKey}
              onChange={onImportedKeyChange}
              placeholder="0x…"
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-gray-900 dark:text-white text-sm font-mono pr-10 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              tabIndex={-1}
            >
              {showKey
                ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              }
            </button>
          </div>
          {importError && <p className="text-red-500 text-xs">{importError}</p>}
          <div className="flex items-start gap-2 bg-amber-900/10 border border-amber-600/30 rounded p-2.5">
            <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Stored only in your browser's local storage — never sent to any server.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 2: Builder Fee Approval ────────────────────────────────────────────
function StepBuilderFee({ config, approving, error, onApprove }) {
  const feeRate = config?.builderFeeRate || '0.0100%';
  return (
    <div className="space-y-4">
      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 mt-0.5 text-purple-500 dark:text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-200">
              Approve Katbot Automation Fee
            </h4>
            <p className="mt-1 text-sm text-purple-700 dark:text-purple-300">
              Sign one MetaMask message authorising Katbot to charge a maximum of{' '}
              <strong className="text-purple-900 dark:text-purple-100">{feeRate}</strong> per trade. This is how Katbot earns revenue
              for running your automated strategies.
            </p>
            <ul className="mt-2 text-xs text-purple-600 dark:text-purple-400 space-y-1 list-disc list-inside">
              <li>No on-chain transaction — a single off-chain signature only</li>
              <li>Fee is capped at {feeRate} (1 basis point)</li>
              <li>You can revoke this at any time on Hyperliquid</li>
            </ul>
          </div>
        </div>
      </div>

      {error && <ErrorBox message={error} />}

      <button
        type="button"
        onClick={onApprove}
        disabled={approving}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md disabled:bg-purple-400 disabled:cursor-not-allowed transition-colors"
      >
        {approving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Waiting for signature…
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
            Sign with MetaMask
          </>
        )}
      </button>
    </div>
  );
}

// ─── Step 3: Done ────────────────────────────────────────────────────────────
function StepDone({ portfolioName, isHyperliquid, isTestnet }) {
  return (
    <div className="flex flex-col items-center text-center space-y-4 py-4">
      {/* Big green check */}
      <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
        <svg className="w-12 h-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div>
        <h4 className="text-xl font-bold text-gray-900 dark:text-white">Congratulations!</h4>
        <p className="mt-1 text-gray-600 dark:text-gray-300 text-sm">
          Portfolio <strong className="text-gray-900 dark:text-white">"{portfolioName}"</strong> is ready to trade.
        </p>
        {isHyperliquid && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {isTestnet ? 'Connected to Hyperliquid Testnet' : 'Connected to Hyperliquid Mainnet'}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Shared error box ────────────────────────────────────────────────────────
function ErrorBox({ message }) {
  return (
    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
      <p className="text-sm text-red-800 dark:text-red-200">{message}</p>
    </div>
  );
}

// ─── Main wizard component ───────────────────────────────────────────────────
export const CreatePortfolioForm = ({
  config,
  onPortfolioCreated,
  onRequestAgentOnboard,
  hyperliquidMainnetCount = 0,
  hyperliquidTestnetCount = 0,
  isOpen: controlledIsOpen,
  onClose: controlledOnClose,
  initialType,
}) => {
  const isControlled = controlledIsOpen !== undefined;
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const [step, setStep] = useState(STEP_INFO);

  const [formData, setFormData] = useState({
    name: '',
    amount: 10000,
    portfolio_type: initialType || 'HL_PAPER',
    is_testnet: true,
    primary_agent_id: '',
  });

  // State tracking created portfolio / agent
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [createdAgent, setCreatedAgent] = useState(null);     // { address, isTestnet, portfolioName, portfolioId }
  const [preGeneratedAgent, setPreGeneratedAgent] = useState(null); // { privateKey, address }

  // Agent step sub-state
  const [agentMode, setAgentMode] = useState('generated'); // 'generated' | 'import'
  const [importedKey, setImportedKey] = useState('');
  const [importError, setImportError] = useState('');

  // Builder fee step sub-state
  const [builderFeeApproving, setBuilderFeeApproving] = useState(false);
  const [builderFeeError, setBuilderFeeError] = useState(null);

  // Agents list for primary agent selector
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const { showSuccess, showError } = useModalAlert();
  const { isConnected, signer, network, jwtToken } = useWallet();

  const setPortfolioName   = useInstanceStore((state) => state.setPortfolioName);
  const setPortfolioId     = useInstanceStore((state) => state.setPortfolioId);

  const apiUrl = config?.apiBaseUrl
    ? `${config.apiBaseUrl}/portfolio`
    : 'http://localhost:8000/portfolio';

  const isHyperliquid = formData.portfolio_type === 'HYPERLIQUID';

  // ── Generate agent key when switching to HYPERLIQUID ──────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const generate = async () => {
      if (isHyperliquid) {
        try {
          const { ethers } = await import('ethers');
          const wallet = ethers.Wallet.createRandom();
          setPreGeneratedAgent({ privateKey: wallet.privateKey, address: wallet.address });
        } catch (err) {
          console.error('Failed to generate agent key:', err);
          setError('Failed to generate agent key. Please try again.');
        }
      } else {
        setPreGeneratedAgent(null);
      }
    };
    generate();
  }, [formData.portfolio_type, isOpen]);

  // ── Fetch agents list ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !jwtToken) return;
    const fetchAgents = async () => {
      setLoadingAgents(true);
      try {
        const base = config?.apiBaseUrl || 'http://localhost:8000';
        const res = await fetch(`${base}/agents`, {
          headers: { Authorization: `Bearer ${jwtToken}` },
        });
        if (res.ok) setAgents(await res.json());
      } catch (err) {
        console.error('Failed to fetch agents:', err);
      } finally {
        setLoadingAgents(false);
      }
    };
    fetchAgents();
  }, [isOpen, jwtToken, config]);

  // ── ESC to close ──────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && isOpen) handleCancel(); };
    if (isOpen) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'amount' ? parseFloat(value) || 0 : value),
    }));
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('Copied!', 'Agent address copied to clipboard', { autoClose: true });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const resetWizard = () => {
    setStep(STEP_INFO);
    setFormData({ name: '', amount: 10000, portfolio_type: 'HL_PAPER', is_testnet: true, primary_agent_id: '' });
    setPreGeneratedAgent(null);
    setCreatedAgent(null);
    setAgentMode('generated');
    setImportedKey('');
    setImportError('');
    setBuilderFeeError(null);
    setError(null);
  };

  const handleCancel = () => {
    resetWizard();
    if (isControlled) {
      controlledOnClose();
    } else {
      setInternalIsOpen(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleCancel();
  };

  // ── Step 0 → submit portfolio creation, then advance ─────────────────────
  const handleSubmitInfo = async () => {
    setError(null);

    if (!formData.name.trim()) { setError('Portfolio name is required'); return; }
    if (formData.portfolio_type === 'HL_PAPER' && formData.amount <= 0) {
      setError('Amount must be greater than 0'); return;
    }
    if (isHyperliquid && !isConnected) {
      setError('Please connect your wallet to create a Hyperliquid portfolio'); return;
    }
    if (isHyperliquid) {
      if (formData.is_testnet && hyperliquidTestnetCount >= 1) {
        showError('Hyperliquid Testnet Limit', 'You can only have one Hyperliquid Testnet portfolio. Please delete your existing one first.');
        return;
      }
      if (!formData.is_testnet && hyperliquidMainnetCount >= 1) {
        showError('Hyperliquid Mainnet Limit', 'You can only have one Hyperliquid Mainnet portfolio. Please delete your existing one first.');
        return;
      }
    }

    try {
      setIsSubmitting(true);

      const body = {
        name: formData.name.trim(),
        portfolio_type: formData.portfolio_type,
      };
      if (formData.primary_agent_id) body.primary_agent_id = parseInt(formData.primary_agent_id, 10);
      if (formData.portfolio_type === 'HL_PAPER') body.amount = formData.amount;
      if (isHyperliquid) {
        body.is_testnet = formData.is_testnet;
        body.arbitrum_rpc_url = formData.is_testnet ? 'https://sepolia-rollup.arbitrum.io/rpc' : null;
        if (preGeneratedAgent) {
          body.agent_private_key = preGeneratedAgent.privateKey;
          body.agent_address     = preGeneratedAgent.address;
        }
      }

      const resp = await fetch(apiUrl, {
        method: 'POST',
        headers: { accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Failed to create portfolio: ${resp.status} ${txt}`);
      }

      const newPortfolio = await resp.json();

      if (isHyperliquid && newPortfolio.agent_address) {
        // Store key in localStorage under the correct key
        const storageKey = `hyperliquid_agent_${newPortfolio.is_testnet ? 'testnet' : 'mainnet'}_${newPortfolio.id}`;
        localStorage.setItem(storageKey, newPortfolio.agent_private_key);

        setCreatedAgent({
          address:       newPortfolio.agent_address,
          isTestnet:     newPortfolio.is_testnet,
          portfolioName: newPortfolio.name,
          portfolioId:   newPortfolio.id,
        });

        setStep(STEP_AGENT);
      } else {
        // Paper portfolio — if no agent was selected, hand off to agent onboard wizard
        if (!formData.primary_agent_id && onRequestAgentOnboard) {
          resetWizard();
          if (isControlled) controlledOnClose?.(); else setInternalIsOpen(false);
          onRequestAgentOnboard(newPortfolio);
          if (onPortfolioCreated) onPortfolioCreated({ name: newPortfolio.name, id: newPortfolio.id });
          return;
        }
        setCreatedAgent({
          portfolioName: newPortfolio.name,
          portfolioId:   newPortfolio.id,
          isTestnet:     false,
        });
        setStep(STEP_DONE);
      }
    } catch (err) {
      console.error('Error creating portfolio:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step 1 → validate agent key choice, advance to builder fee ───────────
  const handleAgentNext = () => {
    if (agentMode === 'import') {
      const key = importedKey.trim();
      if (!key) { setImportError('Please enter your agent private key.'); return; }
      if (!key.startsWith('0x') || key.length !== 66) {
        setImportError('Invalid format — expected a 0x-prefixed 32-byte hex string (66 chars).'); return;
      }
      // Store the imported key in localStorage
      setAgentPrivateKey(createdAgent.portfolioId, key, createdAgent.isTestnet);
      setImportError('');
    }
    // Advance to builder fee step
    setBuilderFeeError(null);
    setStep(STEP_BUILDER);
  };

  // ── Step 2 → approve builder fee ─────────────────────────────────────────
  const handleApproveBuilderFee = async () => {
    if (!createdAgent || !signer) return;

    const builderAddress = config?.builderAddress || '0xDdf3A93522D503a43f802BeB4882BfF18D940a20';
    const feeRate        = config?.builderFeeRate  || '0.0100%';

    try {
      setBuilderFeeApproving(true);
      setBuilderFeeError(null);

      const nonce      = Date.now();
      const isMainnet  = !createdAgent.isTestnet;
      const walletChainId  = network?.chainId ?? (isMainnet ? 42161 : 421614);
      const signatureChainId = '0x' + walletChainId.toString(16);

      const domain = {
        name: 'HyperliquidSignTransaction',
        version: '1',
        chainId: walletChainId,
        verifyingContract: '0x0000000000000000000000000000000000000000',
      };

      const types = {
        'HyperliquidTransaction:ApproveBuilderFee': [
          { name: 'hyperliquidChain', type: 'string'  },
          { name: 'maxFeeRate',       type: 'string'  },
          { name: 'builder',          type: 'address' },
          { name: 'nonce',            type: 'uint64'  },
        ],
      };

      const action = {
        type:              'approveBuilderFee',
        hyperliquidChain:  isMainnet ? 'Mainnet' : 'Testnet',
        signatureChainId,
        maxFeeRate:        feeRate,
        builder:           builderAddress,
        nonce,
      };

      const rawSig = await signer._signTypedData(domain, types, {
        hyperliquidChain: action.hyperliquidChain,
        maxFeeRate:       action.maxFeeRate,
        builder:          action.builder,
        nonce:            action.nonce,
      });

      const r = rawSig.slice(0, 66);
      const s = '0x' + rawSig.slice(66, 130);
      const v = parseInt(rawSig.slice(130, 132), 16);

      const approveUrl = `${config?.apiBaseUrl || 'http://localhost:8000'}/portfolio/${createdAgent.portfolioId}/approve-builder-fee`;
      const resp = await fetch(approveUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken}` },
        body: JSON.stringify({ action, signature: { r, s, v }, nonce }),
      });

      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(`Approval failed: ${resp.status} ${errText}`);
      }

      setStep(STEP_DONE);
    } catch (err) {
      console.error('Builder fee approval error:', err);
      setBuilderFeeError(`Fee approval failed: ${err.message}. Please try again.`);
    } finally {
      setBuilderFeeApproving(false);
    }
  };

  // ── Finish — called from Done step button ─────────────────────────────────
  const handleFinish = () => {
    if (!createdAgent) return;
    setPortfolioName(createdAgent.portfolioName);
    setPortfolioId(createdAgent.portfolioId);
    if (onPortfolioCreated) onPortfolioCreated({ name: createdAgent.portfolioName, id: createdAgent.portfolioId });
    resetWizard();
    if (isControlled) {
      controlledOnClose();
    } else {
      setInternalIsOpen(false);
    }
  };

  // ── Step titles ───────────────────────────────────────────────────────────
  const stepTitle = () => {
    if (step === STEP_INFO)    return 'Create New Portfolio';
    if (step === STEP_AGENT)   return 'Agent Wallet Setup';
    if (step === STEP_BUILDER) return 'Approve Automation Fee';
    return 'Setup Complete';
  };

  // ── Back navigation ───────────────────────────────────────────────────────
  const canGoBack = step === STEP_AGENT || step === STEP_BUILDER;
  const handleBack = () => {
    if (step === STEP_AGENT)   setStep(STEP_INFO);
    if (step === STEP_BUILDER) setStep(STEP_AGENT);
  };

  return (
    <>
      {/* Trigger button */}
      {!isControlled && (
        <button
          onClick={() => setInternalIsOpen(true)}
          className="w-full max-w-[100px] sm:w-auto px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:border-blue-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-2 whitespace-nowrap"
        >
          <div className="flex items-center justify-center sm:justify-start space-x-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Create</span>
          </div>
        </button>
      )}

      {/* Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto"
          onClick={handleBackdropClick}
        >
          <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 my-4 sm:my-0">

            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{stepTitle()}</h3>
              <button
                onClick={handleCancel}
                disabled={isSubmitting || builderFeeApproving}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-40"
              >
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 pt-5 pb-2">
              <StepIndicator currentStep={step} isHyperliquid={isHyperliquid} />

              {step === STEP_INFO && (
                <StepInfo
                  formData={formData}
                  onChange={handleInputChange}
                  onSuggestName={(name) => setFormData((prev) => ({ ...prev, name }))}
                  agents={agents}
                  loadingAgents={loadingAgents}
                  error={error}
                  isSubmitting={isSubmitting}
                />
              )}

              {step === STEP_AGENT && (
                <StepAgent
                  createdAgent={createdAgent}
                  agentMode={agentMode}
                  onSetMode={(m) => { setAgentMode(m); setImportError(''); }}
                  importedKey={importedKey}
                  onImportedKeyChange={(e) => { setImportedKey(e.target.value); setImportError(''); }}
                  importError={importError}
                  onCopy={copyToClipboard}
                />
              )}

              {step === STEP_BUILDER && (
                <StepBuilderFee
                  config={config}
                  approving={builderFeeApproving}
                  error={builderFeeError}
                  onApprove={handleApproveBuilderFee}
                />
              )}

              {step === STEP_DONE && (
                <StepDone
                  portfolioName={createdAgent?.portfolioName}
                  isHyperliquid={isHyperliquid}
                  isTestnet={createdAgent?.isTestnet}
                />
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 mt-4">
              {/* Back / Cancel */}
              <div>
                {canGoBack ? (
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={isSubmitting || builderFeeApproving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                  >
                    ← Back
                  </button>
                ) : step !== STEP_DONE ? (
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                ) : null}
              </div>

              {/* Primary action */}
              <div>
                {step === STEP_INFO && (
                  <button
                    type="button"
                    onClick={handleSubmitInfo}
                    disabled={isSubmitting}
                    className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Creating…
                      </span>
                    ) : isHyperliquid ? 'Next →' : 'Create Portfolio'}
                  </button>
                )}

                {step === STEP_AGENT && (
                  <button
                    type="button"
                    onClick={handleAgentNext}
                    disabled={isSubmitting}
                    className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                  >
                    Next →
                  </button>
                )}

                {/* Builder fee step — primary action is embedded in the step body */}
                {step === STEP_BUILDER && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">Signature required above</span>
                )}

                {step === STEP_DONE && (
                  <button
                    type="button"
                    onClick={handleFinish}
                    disabled={isSubmitting}
                    className="px-5 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                  >
                    Go to Portfolio
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Portfolio Selector (default export — unchanged) ─────────────────────────
export default function PortfolioSelector({ config }) {
  const setPortfolioName   = useInstanceStore((state) => state.setPortfolioName);
  const portfolioName      = useInstanceStore((state) => state.instance.portfolioName);
  const setPortfolioId     = useInstanceStore((state) => state.setPortfolioId);
  const bumpAgentKeyVersion = useInstanceStore((state) => state.bumpAgentKeyVersion);
  const hasHydrated        = useInstanceStore((state) => state._hasHydrated);
  const { showSuccess, showError, showWarning } = useModalAlert();

  const apiUrl = config?.apiBaseUrl
    ? `${config.apiBaseUrl}/portfolio`
    : 'http://localhost:8000/portfolio';

  const [portfolios, setPortfolios] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agentKeyModalOpen, setAgentKeyModalOpen] = useState(false);
  const [pendingPortfolio, setPendingPortfolio] = useState(null);

  // Migrate from old localStorage key
  useEffect(() => {
    const oldKey = 'katbot-selected-portfolio';
    if (localStorage.getItem(oldKey)) {
      localStorage.removeItem(oldKey);
    }
  }, []);

  useEffect(() => {
    if (!hasHydrated) return;
    fetchPortfolios();
  }, [hasHydrated]);

  const fetchPortfolios = async () => {
    try {
      setIsLoading(true);
      const resp = await fetch(apiUrl, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
      if (!resp.ok) throw new Error(`API call failed: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      setPortfolios(data);
      setError(null);

      const storedId   = portfolioId;
      const storedName = portfolioName;

      if (storedId) {
        const existing = data.find((p) => p.id === storedId);
        if (existing) {
          if (existing.name !== storedName) setPortfolioName(existing.name);
        } else {
          setPortfolioName(null);
          setPortfolioId(null);
        }
      } else if (data.length === 1 && data[0].name === 'default') {
        setPortfolioName(data[0].name);
        setPortfolioId(data[0].id);
      } else if (data.length > 0 && !storedName) {
        setPortfolioName(data[0].name);
        setPortfolioId(data[0].id);
      }
    } catch (err) {
      console.error('Error fetching portfolios:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!portfolioId || !portfolioName) return;
    showWarning(
      'Delete Portfolio',
      `Are you sure you want to delete portfolio "${portfolioName}"?\n\nThis action cannot be undone.`,
      {
        showCancel: true,
        confirmText: 'Delete Portfolio',
        cancelText: 'Cancel',
        onConfirm: async () => {
          try {
            setIsLoading(true);
            setError(null);
            const delResp = await fetch(`${apiUrl}/${portfolioId}`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
            });
            if (!delResp.ok) throw new Error(`Delete failed: ${delResp.status} ${delResp.statusText}`);
            await fetchPortfolios();
            const remaining = portfolios.filter((p) => p.id !== portfolioId);
            if (remaining.length > 0) {
              setPortfolioName(remaining[0].name);
              setPortfolioId(remaining[0].id);
            } else {
              setPortfolioName(null);
              setPortfolioId(null);
            }
            showSuccess('Portfolio Deleted', `Portfolio "${portfolioName}" has been deleted.`, { autoClose: true });
          } catch (err) {
            console.error('Error deleting portfolio:', err);
            showError('Delete Failed', `Failed to delete portfolio: ${err.message}`, { autoClose: true });
          } finally {
            setIsLoading(false);
          }
        },
      }
    );
  };

  const handleSelect = (e) => {
    const selected          = e.target.value;
    const selectedPortfolio = portfolios.find((p) => p.name === selected);
    const selectedId        = selectedPortfolio?.id || null;

    if (selectedPortfolio?.portfolio_type === 'HYPERLIQUID' && selectedId && !hasAgentPrivateKey(selectedId)) {
      setPendingPortfolio(selectedPortfolio);
      setAgentKeyModalOpen(true);
      return;
    }

    setPortfolioName(selected);
    setPortfolioId(selectedId);
  };

  return (
    <div className="p-1 space-y-3 w-full">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">

        <div className="w-full sm:w-auto">
          <CreatePortfolioForm config={config} onPortfolioCreated={handlePortfolioCreated} />
        </div>
        {/* Type badge */}
        {(() => {
          const sel = portfolios.find((p) => p.name === portfolioName);
          if (!sel) return null;
          if (sel.portfolio_type === 'HL_PAPER') {
            return (
              <span className="flex sm:inline-flex w-full sm:w-auto justify-center sm:justify-start items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-amber-900/40 text-amber-300 border border-amber-700/50 whitespace-nowrap">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                PAPER
              </span>            );
          }
          if (sel.portfolio_type === 'HYPERLIQUID') {
            return (
              <span className={`flex sm:inline-flex w-full sm:w-auto justify-center sm:justify-start items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                sel.is_testnet
                  ? 'bg-purple-900/40 text-purple-300 border border-purple-700/50'
                  : 'bg-emerald-900/40 text-emerald-300 border border-emerald-700/50'
              }`}>
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {sel.is_testnet ? 'HL TEST' : 'HL MAIN'}
              </span>            );
          }
          return null;
        })()}

        {/* Portfolio select */}
        <div className="relative w-full sm:flex-1">
          <select
            value={portfolioName || ''}
            onChange={handleSelect}
            disabled={isLoading}
            className={`w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg px-4 py-1 pr-10 transition-colors appearance-none cursor-pointer
              ${isLoading ? 'opacity-50 cursor-not-allowed' : 'focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 hover:border-gray-400 dark:hover:border-gray-500'}`}
          >
            <option value="" className="text-gray-500 dark:text-gray-400">
              {isLoading ? 'Loading portfolios…' : '-- Choose Portfolio --'}
            </option>
            {[...portfolios]
              .map((p) => {
                const isPaper = p.portfolio_type === 'HL_PAPER';
                const isHL    = p.portfolio_type === 'HYPERLIQUID';
                const prefix  = isPaper ? 'PAPER' : isHL ? (p.is_testnet ? 'HLTEST' : 'HLMAIN') : '';
                return { ...p, displayName: prefix ? `${prefix}-${p.name}` : p.name };
              })
              .sort((a, b) => a.displayName.localeCompare(b.displayName))
              .map((p) => (
                <option key={p.id} value={p.name} className="text-gray-900 dark:text-white bg-white dark:bg-gray-700">
                  {p.displayName}
                </option>
              ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            {isLoading
              ? <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              : <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            }
          </div>
        </div>

        {/* Delete button */}
        {portfolioId && (
          <button
            onClick={handleDelete}
            disabled={isLoading}
            className={`w-full sm:w-auto px-3 py-2 text-sm font-medium rounded-lg border transition-colors whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-800
              ${isLoading
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 border-gray-200 dark:border-gray-700 cursor-not-allowed'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 hover:border-red-300 dark:hover:red-700'}`}
            title={`Delete portfolio: ${portfolioName}`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                <span>Deleting…</span>
              </div>            ) : (
              <div className="flex items-center justify-center sm:justify-start gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Delete</span>
              </div>            )}
          </button>
        )}
      </div>
      {error && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">
            <span className="font-medium">Error:</span> {error.message}
          </p>
        </div>      )}

      {/* Agent key gate modal for selecting existing HYPERLIQUID portfolios */}
      <AgentKeyModal
        isOpen={agentKeyModalOpen}
        portfolioId={pendingPortfolio?.id}
        isTestnet={pendingPortfolio?.is_testnet}
        portfolioName={pendingPortfolio?.name}
        onSave={() => {
          setPortfolioName(pendingPortfolio.name);
          setPortfolioId(pendingPortfolio.id);
          bumpAgentKeyVersion();
          setAgentKeyModalOpen(false);
          setPendingPortfolio(null);
        }}
        onClose={() => {
          setAgentKeyModalOpen(false);
          setPendingPortfolio(null);
        }}
      />
    </div>  );
}
